import { ComponentProps, ReactElement, ReactNode } from 'react';
import { PieArcDatum as d3PieArcDatum } from 'd3-shape';
import {
  ChartDimensions,
  ChartSize,
  ChartTheme,
  ArcDatum,
  TooltipVariant,
  CategoryData,
  CategoryDatum,
  Statistics,
} from '../../types';
import { Theme, ValueOf } from '@redsift/design-system';
import { ArcProps } from '../Arc';
import { ChartContainerProps } from '../ChartContainer';
import { LegendProps } from '../Legend';
import { SortingMethod } from '../../utils';
import { AnchorProps } from '../DataPoint';

/**
 * Component variant.
 */
export const PieChartVariant = {
  plain: 'plain',
  spaced: 'spaced',
  donut: 'donut',
  spacedDonut: 'spacedDonut',
} as const;
export type PieChartVariant = ValueOf<typeof PieChartVariant>;

/**
 * Component's labels variant.
 */
export const PieChartLegendVariant = {
  none: 'none',
  internal: 'internal',
  externalLabel: 'externalLabel',
  externalLabelValue: 'externalLabelValue',
  externalLabelPercent: 'externalLabelPercent',
  custom: 'custom',
} as const;
export type PieChartLegendVariant = ValueOf<typeof PieChartLegendVariant>;

interface LocaleText {
  emptyChartText: string;
}

export type PieChartDimensions = ChartDimensions & {
  smallFontSize: number;
  fontSize: number;
  innerRadius: number;
};

/**
 * Component props.
 */
export interface PieChartProps extends ChartContainerProps {
  /** Number of categories to use, the rest being put into a new category called "Others". */
  caping?: number;
  /** Dataset to use to generate the chart, should be a list of objects containing a key and a value. */
  data?: CategoryData;
  /** Component to use if the chart is empty (replacing the default one). */
  emptyComponent?: ReactNode;
  /** Method used to define the anchor props to use when the slice is a navigation link. */
  getSliceAnchorProps?: (datum: ArcDatum) => AnchorProps;
  /** Whether the pie or donut is cut in half or not. */
  isHalf?: boolean;
  /** Method to determine whether a slice is selected or not. */
  isSliceSelected?: (datum: ArcDatum) => boolean | undefined;
  /** Method to override the data labels. */
  labelDecorator?: (
    datum: ArcDatum,
    props?: { index?: number; isSelected?: boolean; color?: string }
  ) => string | ReactElement;
  /** Method modifying what's displayed within the legend when the legendVariant is "custom". */
  legendDecorator?: (
    datum: ArcDatum,
    props?: { index?: number; isSelected?: boolean; color?: string }
  ) => string | ReactElement;
  /** Define whether the labels should be displayed inside or outside the charts and if they should contain raw or percentage values. If set to "custom", use `legendDecorator` to customize it. */
  legendVariant?: PieChartLegendVariant;
  /** Props to forward to the Legend block. Can be used to make the legend selectable. */
  legendProps?: Omit<LegendProps, 'data' | 'ref' | 'variant'> & { extraLegendItems?: Statistics };
  /** Labels and texts. */
  localeText?: LocaleText;
  /** Method to be called on a click on a slice. For a navigation link, please use `getSliceAnchorProps` instead. */
  onSliceClick?: (datum: ArcDatum) => void;
  /** Whether a category should be displayed for categories that has been removed by the caping option. Optionaly, this prop can be used to change the label of this category. */
  others?: boolean | string;
  /** PieChart size. */
  size?: ChartSize | PieChartDimensions;
  /** Native HTML props to forward to each slice. */
  sliceProps?: Omit<ComponentProps<'g'>, 'onClick' | 'ref' | 'width'>;
  /** Slice role. If an onClick is provided, the slices will have the role `button`. For a navigation link, please use `getSliceAnchorProps` instead. */
  sliceRole?: ArcProps['role'];
  /** Define how to sort categories. */
  sortingMethod?: SortingMethod;
  /** Secondary text to be displayed in the middle of the chart, between the main text and subtext. Recommended to be used with donut variants only. */
  middleText?: string | ReactElement | ((data: d3PieArcDatum<CategoryDatum>[], total: number) => string | ReactElement);
  /** Secondary text to be displayed in the middle of the chart, above the main text. Recommended to be used with `donut` variants only. */
  subtext?: string | ReactElement | ((data: d3PieArcDatum<CategoryDatum>[], total: number) => string | ReactElement);
  /** Main text to be displayed in the middle of the chart. Recommended to be used with `donut` variants only. */
  text?: string | ReactElement | ((data: d3PieArcDatum<CategoryDatum>[], total: number) => string | ReactElement);
  /** Color palette to use. You can choose among the list of available color palette or also choose to use the success/warning/danger theme for which you have to specify which key corresponds to which status.  */
  colorTheme?: ChartTheme;
  /** PieChart variant. */
  variant?: PieChartVariant;
  /** Method modifying what's displayed within the tooltip when the tooltipVariant is "custom".  */
  tooltipDecorator?: (
    data: ArcDatum,
    props?: { index?: number; isSelected?: boolean; color?: string }
  ) => string | ReactElement;
  /** Tooltip variant. */
  tooltipVariant?: TooltipVariant;
  /** Theme. */
  theme?: Theme;
}

export type StyledPieChartProps = PieChartProps & {};
